import { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../../context/AuthContext';
import { useLocation, useNavigate } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Textarea } from '../../components/ui/textarea';
import { Badge } from '../../components/ui/badge';
import { Skeleton } from '../../components/ui/skeleton';
import { 
  Banknote, 
  CheckCircle2, 
  AlertTriangle,
  Loader2,
  Clock,
  TrendingDown,
  TrendingUp,
  Minus,
  Send,
  ClipboardList,
  XCircle,
  ArrowRight
} from 'lucide-react';
import { toast } from 'sonner';

const getDiscColor = (disc) => {
  if (disc === 0) return { bg: 'bg-emerald-50', text: 'text-emerald-600', border: 'border-emerald-200', label: 'Balanced' };
  const a = Math.abs(disc);
  if (a <= 5) return { bg: 'bg-amber-50', text: 'text-amber-600', border: 'border-amber-200', label: 'Warning' };
  return { bg: 'bg-red-50', text: 'text-red-600', border: 'border-red-200', label: 'Alert' };
};

const logTypeLabels = { start: 'Start', mid_check: 'Mid-Check', end: 'End' };
const statusBadgeStyles = {
  balanced: 'bg-emerald-100 text-emerald-800',
  warning: 'bg-amber-100 text-amber-800',
  alert: 'bg-red-100 text-red-800',
};

export default function TillChecksPage() {
  const { api } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [auditLogs, setAuditLogs] = useState([]);
  const [auditSummary, setAuditSummary] = useState(null);
  const [selectedShift, setSelectedShift] = useState(null);

  // Two-step form state
  const [currentAmount, setCurrentAmount] = useState('');
  const [note, setNote] = useState('');
  const [step, setStep] = useState('amount'); // 'amount' | 'explain'
  const [pendingDiscrepancy, setPendingDiscrepancy] = useState(null);
  const [isEndTill, setIsEndTill] = useState(false);

  const fetchShiftAuditData = useCallback(async (shiftId) => {
    try {
      const res = await api.get(`/till-audit/shift/${shiftId}`);
      setAuditLogs(res.data.logs || []);
      setAuditSummary(res.data.summary);
    } catch {
      setAuditLogs([]);
      setAuditSummary(null);
    }
  }, [api]);

  const fetchMyShifts = useCallback(async () => {
    try {
      const response = await api.get('/shifts/my-shifts');
      const active = response.data.find(s => s.status === 'in_progress');
      const completed = response.data.filter(s => s.status === 'completed');
      const target = active || completed[0] || null;
      if (target) {
        setSelectedShift(target);
        fetchShiftAuditData(target.id);
      }
    } catch {
      toast.error('Failed to load shifts');
    } finally {
      setLoading(false);
    }
  }, [api, fetchShiftAuditData]);

  useEffect(() => { fetchMyShifts(); }, [fetchMyShifts]);

  useEffect(() => {
    if (location.state?.shiftId) {
      const shiftId = location.state.shiftId;
      (async () => {
        try {
          const res = await api.get('/shifts/my-shifts');
          const shift = res.data.find(s => s.id === shiftId);
          if (shift) {
            setSelectedShift(shift);
            fetchShiftAuditData(shift.id);
            if (location.state?.submitTillLog) setIsEndTill(true);
          }
        } catch {}
      })();
    }
  }, [location.state, api, fetchShiftAuditData]);

  const hasStart = auditLogs.some(l => l.logType === 'start');
  const hasEnd = auditLogs.some(l => l.logType === 'end');
  const isShiftActive = selectedShift?.status === 'in_progress';
  // Allow submission during active shift, or when redirected from End Shift for final till
  const canSubmit = (isShiftActive || isEndTill) && !hasEnd;

  const getExpectedAmount = () => {
    if (auditLogs.length === 0) return 0;
    return auditLogs[auditLogs.length - 1].actualAmount;
  };

  const resetForm = () => {
    setCurrentAmount('');
    setNote('');
    setStep('amount');
    setPendingDiscrepancy(null);
    setIsEndTill(false);
  };

  // Step 1: Worker enters amount
  const handleAmountSubmit = (e) => {
    e.preventDefault();
    const amount = parseFloat(currentAmount);
    if (isNaN(amount) || amount < 0) { toast.error('Please enter a valid amount'); return; }

    if (!hasStart) {
      // First log → start (always balanced, no discrepancy possible)
      submitToBackend('start', amount, '');
      return;
    }

    const expected = getExpectedAmount();
    const diff = round2(amount - expected);

    if (diff === 0) {
      // Balanced → submit directly
      submitToBackend(isEndTill ? 'end' : 'mid_check', amount, '');
    } else {
      // Discrepancy detected → go to step 2 (explanation)
      setPendingDiscrepancy({ amount, expected, diff });
      setStep('explain');
    }
  };

  // Step 2: Worker provides explanation → submit
  const handleExplanationSubmit = (e) => {
    e.preventDefault();
    if (!note.trim()) { toast.error('Please enter an explanation'); return; }
    submitToBackend(
      isEndTill ? 'end' : 'mid_check',
      pendingDiscrepancy.amount,
      note.trim()
    );
  };

  const submitToBackend = async (logType, amount, explanation) => {
    setSubmitting(true);
    try {
      const endpoint = logType === 'start' ? '/till-audit/start'
        : logType === 'end' ? '/till-audit/end'
        : '/till-audit/mid-check';

      await api.post(endpoint, {
        shiftId: selectedShift.id,
        actualAmount: amount,
        note: explanation
      });

      if (logType === 'start') {
        toast.success('Starting till recorded!');
      } else if (logType === 'end') {
        toast.success('Shift completed successfully! Final till submitted.');
      } else if (pendingDiscrepancy && pendingDiscrepancy.diff !== 0) {
        toast.success('Your explanation has been sent to the manager.');
      } else {
        toast.success('Till check recorded - Balanced!');
      }

      resetForm();
      fetchShiftAuditData(selectedShift.id);

      if (logType === 'end') {
        // Refresh shift data to reflect completed status, then redirect
        try {
          const shiftRes = await api.get('/shifts/my-shifts');
          const updatedShift = shiftRes.data.find(s => s.id === selectedShift.id);
          if (updatedShift) setSelectedShift(updatedShift);
        } catch {}
        setTimeout(() => navigate('/worker/my-shifts'), 1500);
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to submit');
    } finally {
      setSubmitting(false);
    }
  };

  const expected = getExpectedAmount();
  const formatTime = (isoString) => new Date(isoString).toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit' });

  if (loading) {
    return (
      <div className="p-6 space-y-6">
        <Skeleton className="h-10 w-48" /><Skeleton className="h-24" /><Skeleton className="h-48" /><Skeleton className="h-64" />
      </div>
    );
  }

  if (!selectedShift) {
    return (
      <div className="p-6 space-y-6" data-testid="till-checks-no-shift">
        <div>
          <h1 className="text-2xl md:text-3xl font-bold text-slate-900 font-['Public_Sans']" data-testid="till-check-heading">Till Check</h1>
          <p className="text-slate-500 mt-1 text-base">Record and check till amounts.</p>
        </div>
        <Card className="border-0 shadow-sm">
          <CardContent className="p-12 text-center">
            <Banknote className="h-16 w-16 text-slate-300 mx-auto mb-4" />
            <h3 className="text-lg font-medium text-slate-900 mb-2">No active shift</h3>
            <p className="text-slate-500 mb-4">You need to be on a shift to record till amounts.</p>
            <Button className="rounded-full bg-[#DA291C] hover:bg-[#b71c1c]" onClick={() => navigate('/worker/tracker')} data-testid="go-to-tracker-btn">
              Open Today&apos;s Shift
            </Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="p-6 space-y-6" data-testid="till-checks-page">
      <div>
        <h1 className="text-2xl md:text-3xl font-bold text-slate-900 font-['Public_Sans']" data-testid="till-check-heading">Till Check</h1>
        <p className="text-slate-500 mt-1 text-base">Record starting and ending amounts. Explain any difference.</p>
      </div>

      {/* Shift Info */}
      <Card className="border-0 shadow-sm" data-testid="shift-info-card">
        <CardContent className="p-4 flex items-center justify-between">
          <div>
            <h3 className="font-semibold text-slate-900">{selectedShift.branchName}</h3>
            <p className="text-sm text-slate-500">{selectedShift.date} - {selectedShift.scheduledStartTime} to {selectedShift.scheduledEndTime}</p>
          </div>
          <Badge className={`${isShiftActive ? 'bg-emerald-100 text-emerald-800' : 'bg-slate-100 text-slate-800'}`}>
            {isShiftActive ? 'Active' : 'Completed'}
          </Badge>
        </CardContent>
      </Card>

      {/* No Active Shift Notice - shown when viewing a completed shift */}
      {!isShiftActive && !isEndTill && (
        <Card className="border-0 shadow-sm border-l-4 border-l-amber-400" data-testid="no-active-shift-notice">
          <CardContent className="p-4 flex items-start gap-3">
            <AlertTriangle className="h-5 w-5 text-amber-500 shrink-0 mt-0.5" />
            <div className="flex-1">
              <p className="font-medium text-slate-900 text-sm">This shift is completed</p>
              <p className="text-sm text-slate-500 mt-0.5">Till inputs are only available during an active shift. Start a new shift to record till amounts.</p>
            </div>
            <Button size="sm" variant="outline" className="rounded-full text-xs shrink-0" onClick={() => navigate('/worker/tracker')} data-testid="start-shift-cta">
              Go to Tracker
            </Button>
          </CardContent>
        </Card>
      )}

      {/* Completed Summary */}
      {hasEnd && auditSummary && (
        <Card className="border-0 shadow-sm bg-emerald-50" data-testid="audit-complete-summary">
          <CardContent className="p-5">
            <div className="flex items-center gap-3 mb-4">
              <CheckCircle2 className="h-6 w-6 text-emerald-600" />
              <h3 className="font-semibold text-emerald-800">Till Check Complete</h3>
            </div>
            <div className="grid grid-cols-3 gap-4 text-center">
              <div>
                <p className="text-sm text-slate-500">Start</p>
                <p className="font-bold text-slate-900">&pound;{auditSummary.startAmount?.toFixed(2)}</p>
              </div>
              <div>
                <p className="text-sm text-slate-500">End</p>
                <p className="font-bold text-slate-900">&pound;{auditSummary.endAmount?.toFixed(2)}</p>
              </div>
              <div>
                <p className="text-sm text-slate-500">Net</p>
                <p className={`font-bold ${auditSummary.netDiscrepancy === 0 ? 'text-emerald-600' : auditSummary.netDiscrepancy < 0 ? 'text-red-600' : 'text-amber-600'}`}>
                  {auditSummary.netDiscrepancy === 0 ? 'Balanced' : `\u00a3${Math.abs(auditSummary.netDiscrepancy).toFixed(2)} ${auditSummary.netDiscrepancy < 0 ? 'short' : 'over'}`}
                </p>
              </div>
            </div>
          </CardContent>
        </Card>
      )}

      {/* ===== LOG TILL AMOUNT ===== */}
      {canSubmit && (
        <Card className="border-0 shadow-sm" data-testid="log-till-card">
          <CardHeader className="pb-3">
            <CardTitle className="text-lg font-semibold font-['Public_Sans'] flex items-center gap-2">
              <Banknote className="h-5 w-5 text-[#DA291C]" />
              {!step || step === 'amount' ? (isEndTill ? 'Ending Till' : (!hasStart ? 'Starting Till' : 'Log Till Amount')) : 'Log Till Amount'}
            </CardTitle>
          </CardHeader>
          <CardContent>
            {/* STEP 1: Enter Amount */}
            {step === 'amount' && (
              <form onSubmit={handleAmountSubmit} className="space-y-4">
                {!hasStart && (
                  <p className="text-sm text-slate-500" data-testid="starting-till-hint">Record the amount before serving customers.</p>
                )}
                {hasStart && isEndTill && (
                  <p className="text-sm text-slate-500" data-testid="ending-till-hint">Record the amount after completing your shift.</p>
                )}
                {hasStart && (
                  <div className="p-3 bg-slate-50 rounded-xl" data-testid="expected-amount-display">
                    <p className="text-sm text-slate-500">Expected (from last check)</p>
                    <p className="text-2xl font-bold text-slate-900">&pound;{expected.toFixed(2)}</p>
                  </div>
                )}

                <div className="space-y-2">
                  <Label className="text-slate-700 font-medium text-base">
                    {!hasStart ? 'Enter starting till amount' : (isEndTill ? 'Enter ending till amount' : 'Enter current till amount')}
                  </Label>
                  <Input
                    type="number" step="0.01" min="0" placeholder="0.00"
                    value={currentAmount} onChange={(e) => setCurrentAmount(e.target.value)}
                    required autoFocus
                    className="h-14 rounded-xl bg-slate-50 border-slate-200 text-xl font-medium"
                    data-testid="till-amount-input"
                    inputMode="decimal"
                  />
                </div>

                <div className="flex gap-3">
                  <Button
                    type="submit"
                    className="flex-1 h-12 rounded-full bg-[#DA291C] hover:bg-[#b91f13] text-white font-medium"
                    disabled={submitting || !currentAmount}
                    data-testid="submit-till-btn"
                  >
                    {submitting ? <Loader2 className="h-5 w-5 animate-spin" /> : (
                      <><Send className="h-4 w-4 mr-2" />{hasStart ? 'Submit Check' : 'Record Start'}</>
                    )}
                  </Button>
                  {hasStart && !isEndTill && (
                    <Button
                      type="button" variant="outline"
                      className="h-12 rounded-full border-2 border-slate-300 px-6 font-medium"
                      onClick={() => setIsEndTill(true)}
                      data-testid="switch-end-till-btn"
                    >
                      End Till
                    </Button>
                  )}
                  {isEndTill && (
                    <Button
                      type="button" variant="ghost"
                      className="h-12 rounded-full text-slate-500"
                      onClick={() => setIsEndTill(false)}
                    >
                      Cancel End
                    </Button>
                  )}
                </div>

                {isEndTill && (
                  <p className="text-xs text-amber-600 text-center font-medium">Submitting as final till amount (End of Shift)</p>
                )}
                {!hasStart && (
                  <p className="text-xs text-slate-400 text-center">This will be your starting baseline for the shift</p>
                )}
              </form>
            )}

            {/* STEP 2: Explain Discrepancy */}
            {step === 'explain' && pendingDiscrepancy && (
              <div className="space-y-4" data-testid="discrepancy-explanation-step">
                {/* Discrepancy Alert Box */}
                <div className={`p-4 rounded-xl ${getDiscColor(pendingDiscrepancy.diff).bg} border ${getDiscColor(pendingDiscrepancy.diff).border}`}
                  data-testid="discrepancy-alert"
                >
                  <div className="flex items-center gap-2 mb-3">
                    <AlertTriangle className={`h-5 w-5 ${getDiscColor(pendingDiscrepancy.diff).text}`} />
                    <h3 className={`font-bold ${getDiscColor(pendingDiscrepancy.diff).text}`} data-testid="discrepancy-title">
                      The amounts don&apos;t match.
                    </h3>
                    <Badge className={statusBadgeStyles[Math.abs(pendingDiscrepancy.diff) <= 5 ? 'warning' : 'alert']}>
                      {Math.abs(pendingDiscrepancy.diff) <= 5 ? 'Warning' : 'Alert'}
                    </Badge>
                  </div>
                  <div className="grid grid-cols-3 gap-3 text-center">
                    <div>
                      <p className="text-xs text-slate-500">Expected</p>
                      <p className="font-bold text-slate-900">&pound;{pendingDiscrepancy.expected.toFixed(2)}</p>
                    </div>
                    <div>
                      <p className="text-xs text-slate-500">Recorded</p>
                      <p className="font-bold text-slate-900">&pound;{pendingDiscrepancy.amount.toFixed(2)}</p>
                    </div>
                    <div>
                      <p className="text-xs text-slate-500">Difference</p>
                      <p className={`font-bold ${getDiscColor(pendingDiscrepancy.diff).text}`}>
                        {pendingDiscrepancy.diff > 0 ? '+' : ''}&pound;{pendingDiscrepancy.diff.toFixed(2)}
                      </p>
                    </div>
                  </div>
                </div>

                {/* Explanation Form */}
                <form onSubmit={handleExplanationSubmit} className="space-y-4">
                  <div className="space-y-2">
                    <Label className="text-slate-700 font-medium text-base">
                      Please briefly explain anything that may have caused this.
                    </Label>
                    <Textarea
                      placeholder="e.g. Customer returned item, cash float adjusted, counting error…"
                      value={note} onChange={(e) => setNote(e.target.value)}
                      rows={3} autoFocus
                      className="rounded-xl bg-slate-50 border-slate-200 resize-none text-base"
                      data-testid="till-note-input"
                    />
                  </div>

                  <div className="flex gap-3">
                    <Button
                      type="button" variant="outline"
                      className="flex-1 h-12 rounded-full border-2"
                      onClick={() => { setStep('amount'); setPendingDiscrepancy(null); }}
                      data-testid="back-to-amount-btn"
                    >
                      <XCircle className="h-4 w-4 mr-2" /> Go back
                    </Button>
                    <Button
                      type="submit"
                      className="flex-1 h-12 rounded-full bg-[#DA291C] hover:bg-[#b91f13] text-white font-medium"
                      disabled={submitting || !note.trim()}
                      data-testid="submit-explanation-btn"
                    >
                      {submitting ? <Loader2 className="h-5 w-5 animate-spin" /> : (
                        <><Send className="h-4 w-4 mr-2" />Send explanation</>
                      )}
                    </Button>
                  </div>

                  <p className="text-xs text-slate-400 text-center">
                    Your explanation will be sent to the manager.
                  </p>
                </form>
              </div>
            )}
          </CardContent>
        </Card>
      )}

      {/* ===== TILL TIMELINE ===== */}
      <Card className="border-0 shadow-sm" data-testid="till-timeline-card">
        <CardHeader className="pb-2">
          <CardTitle className="text-lg font-semibold font-['Public_Sans'] flex items-center gap-2">
            <ClipboardList className="h-5 w-5 text-slate-500" />
            Till Timeline
          </CardTitle>
        </CardHeader>
        <CardContent>
          {auditLogs.length === 0 ? (
            <div className="py-12 text-center" data-testid="till-empty-state">
              <Banknote className="h-14 w-14 text-slate-200 mx-auto mb-4" />
              <h3 className="text-base font-medium text-slate-800 mb-1">No till logs yet</h3>
              <p className="text-sm text-slate-500">Start by entering your till amount above.</p>
            </div>
          ) : (
            <div className="space-y-0 relative" data-testid="till-timeline-entries">
              <div className="absolute left-[11px] top-3 bottom-3 w-0.5 bg-slate-200" />

              {[...auditLogs].reverse().map((log) => {
                const disc = log.discrepancy || 0;
                const colors = getDiscColor(disc);
                const isFirstDisc = auditSummary?.firstDiscrepancyId === log.id;
                const status = log.status || (disc === 0 ? 'balanced' : Math.abs(disc) <= 5 ? 'warning' : 'alert');

                return (
                  <div
                    key={log.id}
                    className={`relative pl-8 py-3 ${isFirstDisc ? 'ring-2 ring-red-300 rounded-lg ml-[-8px] pl-[40px] bg-red-50/50' : ''}`}
                    data-testid={`audit-log-${log.id}`}
                  >
                    <div className={`absolute left-0 top-4 h-6 w-6 rounded-full flex items-center justify-center z-10 ${colors.bg} border-2 ${colors.border}`}>
                      {log.logType === 'start' ? <Banknote className={`h-3 w-3 ${colors.text}`} /> :
                       log.logType === 'end' ? <CheckCircle2 className={`h-3 w-3 ${colors.text}`} /> :
                       disc === 0 ? <Minus className={`h-3 w-3 ${colors.text}`} /> :
                       disc < 0 ? <TrendingDown className={`h-3 w-3 ${colors.text}`} /> :
                       <TrendingUp className={`h-3 w-3 ${colors.text}`} />}
                    </div>

                    <div className="flex items-start justify-between gap-3">
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2 flex-wrap">
                          <Badge className={`${colors.bg} ${colors.text} text-xs`}>{logTypeLabels[log.logType]}</Badge>
                          <Badge className={`${statusBadgeStyles[status] || 'bg-slate-100'} text-xs`}>{status}</Badge>
                          <span className="text-xs text-slate-400">{formatTime(log.timestamp)}</span>
                          {isFirstDisc && <Badge className="bg-red-100 text-red-700 text-xs">First Discrepancy</Badge>}
                        </div>
                        {log.note && <p className="text-sm text-slate-600 mt-1 italic">"{log.note}"</p>}
                      </div>
                      <div className="text-right shrink-0">
                        <p className="text-sm font-bold text-slate-900">&pound;{log.actualAmount.toFixed(2)}</p>
                        {log.logType !== 'start' && (
                          <>
                            <p className={`text-xs font-medium ${colors.text}`}>
                              {disc === 0 ? 'Balanced' : `${disc > 0 ? '+' : ''}\u00a3${disc.toFixed(2)}`}
                            </p>
                            <p className="text-[10px] text-slate-400">exp: &pound;{log.expectedAmount.toFixed(2)}</p>
                          </>
                        )}
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}

function round2(n) { return Math.round(n * 100) / 100; }
